import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  notification,
  Select,
  Table,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import {
  setFiltersUserRequestByDateDTO,
  userRequestByDateListAsync,
} from "../../../features/userRequest/userRequestByDateListSlice";
import {
  cancelUserRequest,
  completeUserRequest,
  deleteUserRequest,
  generateTripLink,
  getUserRequestsExcel,
} from "../../../services/userRequestApi";
import { ExclamationCircleFilled } from "@ant-design/icons";
import {
  AdminTypeId,
  ExportToExcelDescription,
  NoFinishDate,
  NotPaidDescription,
  OperatorTypeId,
  PaidDescription,
  SuperAdminTypeId,
} from "../../../shared/constants/Resources";
import { useNavigate } from "react-router-dom";
import {
  selectCurrentAdminId,
  selectCurrentAdminType,
} from "../../../utils/helpers/selectorHelper";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import {
  IndexStatusTranslations,
  StatusColors,
  StatusTranslations,
} from "../../../shared/constants/Status";
import UserRequestByDateDTO from "../../../shared/dtos/userRequestExportDTO";
import ContainerLayout from "../../common/containerLayout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { removeFromPendingListAsync } from "../../../features/userRequest/pendingCalculationUserRequestSlice";
import { TRIP_STATUS } from "../../../shared/constants/TripStatus";
import { CurrencyDTO } from "../../../shared/dtos/currencyDTO";
import { getCurrencies } from "../../../services/currencyApi";
import { InlineForm } from "../../../shared/components/InlineForm";

const { Column } = Table;

export const UserRequestTable = () => {
  const navigate = useNavigate();
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [userRequestId, setUserRequestId] = useState<number | null>(null);
  const [currencies, setCurrencies] = useState<CurrencyDTO[]>([]);

  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const fetchData = async () => {
    const data = await getCurrencies();
    setCurrencies(data);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    form.resetFields();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const title: string = "Viajes por fecha";
  const {
    filtersUserRequestByDateDTO: filters,
    loading,
    value: {
      userRequestByDateDto: userRequestByDateValue,
      total: userRequestByDateTotal,
    },
  } = useAppSelector((state) => state.userRequestByDateList);

  useEffect(() => {
    dispatch(userRequestByDateListAsync({ ...filters }));
  }, [dispatch, filters]);

  useEffect(() => {
    const newFilters = { ...filters };
    if (newFilters.bookingId) newFilters.bookingId = null;
    dispatch(setFiltersUserRequestByDateDTO(newFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const onPageChange = async (page: number, pageSize: number) => {
    const newFilters = { ...filters, page, take: pageSize };
    dispatch(setFiltersUserRequestByDateDTO(newFilters));
  };

  const handleExcelExport = async () => {
    setIsLoadingExcel(true);
    const result = await getUserRequestsExcel({
      startDate: dayjs(filters.startDate).toDate(),
      endDate: dayjs(filters.endDate).toDate(),
      shouldSendCompany: filters.shouldSendCompany || false,
      adminId:
        selectCurrentAdminType() === AdminTypeId
          ? selectCurrentAdminId()
          : null,
    });
    setIsLoadingExcel(false);
    const link = document.createElement("a");
    link.href = result;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const reloadData = () => {
    const newFilters = {
      ...filters,
      page: 1,
    };
    dispatch(setFiltersUserRequestByDateDTO(newFilters));
  };

  const showDeleteConfirm = async (id: number) => {
    Modal.confirm({
      title: "Estas seguro de borrar este viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await deleteUserRequest({ userRequestId: id }).finally(() => {
          Modal.destroyAll();
        });
        reloadData();
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const showCompleteConfirm = async (id: number) => {
    Modal.confirm({
      title: "Estas seguro de completar este viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await completeUserRequest({ userRequestId: id }).finally(() => {
          Modal.destroyAll();
        });
        reloadData();
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const calculateRequest = async () => {
    const { total, currencyId } = form.getFieldsValue();

    if (!userRequestId || isNaN(total)) {
      alert("Dolares y Pesos deben ser numeros");
      return;
    }

    if (!currencyId) {
      alert("Debe seleccionar una denominacion");
      return;
    }

    const currencyCode = currencies.find(
      (currency) => currency.id === currencyId,
    )?.code;

    try {
      await dispatch(
        removeFromPendingListAsync({
          userRequestId,
          total,
          currencyCode,
        }),
      );
    } catch (e) {
    } finally {
      reloadData();
      setOpenModal(false);
      setUserRequestId(null);
    }
  };

  const showCancelConfirm = async (id: number) => {
    Modal.confirm({
      title: "Estas seguro de cancelar este viaje?",
      icon: <ExclamationCircleFilled />,
      okText: "Si",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        await cancelUserRequest({ userRequestId: id }).finally(() =>
          Modal.destroyAll(),
        );
        reloadData();
      },
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const onFilter = (value: any, filter: any) => {
    const status = filter.status[0];
    const newFilters = {
      ...filters,
      page: value.current,
      status: status === "" ? null : status,
    };
    setCurrentStatus(status);

    dispatch(setFiltersUserRequestByDateDTO(newFilters));
  };

  const items = (record: any, isOperator?: boolean) => {
    if (isOperator)
      return [
        {
          key: "1",
          label: <span>Cancelar</span>,
          onClick: () => showCancelConfirm(record.id),
        },
      ];
    const status = StatusTranslations.find(
      (x) => x.translation === record.status,
    );

    return [
      {
        key: "1",
        label: <span>Editar</span>,
        onClick: () =>
          navigate(WebRoutes.TripEditRoute, {
            replace: true,
            state: {
              id: record.id,
            },
          }),
      },
      {
        key: "2",
        label: <span>Copiar enlace del viaje</span>,
        onClick: async () => {
          let url: string;
          const bookingIdEncrypted = record.bookingIdEncrypted;

          if (bookingIdEncrypted) {
            url = `${process.env.REACT_APP_AIRPORT_QR_CODE_URL}${bookingIdEncrypted}`;
            notification.success({
              placement: "bottom",
              message: "Enlace generado correctamente",
            });
          } else url = await generateTripLink(record.bookingId);

          navigator.clipboard.writeText(url);
        },
      },
      {
        key: "3",
        label: <span>Reenviar cierre</span>,
        onClick: () => {
          setUserRequestId(record.id);
          setOpenModal(true);
        },
      },
      ...(status?.name === TRIP_STATUS.Completed
        ? []
        : [
            {
              key: "4",
              label: <span>Completar</span>,
              onClick: () => showCompleteConfirm(record.id),
            },
          ]),
      {
        key: "5",
        label: <span>Cancelar</span>,
        onClick: () => showCancelConfirm(record.id),
      },
      {
        key: "6",
        label: <span>Borrar</span>,
        onClick: () => showDeleteConfirm(record.id),
      },
    ];
  };

  const onFieldsChange = debounce((changedFields: any) => {
    const field = changedFields[0]?.name[0];
    const value = changedFields[0]?.value;
    if (field) {
      const newFilters = {
        ...filters,
        page: 1,
        [field]:
          field === "startDate" || field === "endDate"
            ? dayjs(value).toDate().toString()
            : value,
      };
      dispatch(setFiltersUserRequestByDateDTO(newFilters));
    }
  }, 500);

  return (
    <ContainerLayout title={title} showNotification={false}>
      <Col span={24}>
        <Form
          layout="inline"
          initialValues={{
            shouldSendCompany: filters?.shouldSendCompany,
            startDate: dayjs(filters?.startDate),
            endDate: dayjs(filters?.endDate),
          }}
          onFieldsChange={onFieldsChange}
        >
          <Form.Item>
            <Button
              onClick={() => handleExcelExport()}
              loading={isLoadingExcel}
            >
              {ExportToExcelDescription}
            </Button>
          </Form.Item>
          <Form.Item name="startDate">
            <DatePicker allowClear={false} />
          </Form.Item>
          <Form.Item name="endDate">
            <DatePicker allowClear={false} />
          </Form.Item>
          {selectCurrentAdminType() === AdminTypeId ? null : (
            <Form.Item name="shouldSendCompany">
              <Select
                options={[
                  {
                    value: true,
                    label: "Filtrar por compañia",
                  },
                  {
                    value: false,
                    label: "Filtrar por conductor",
                  },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item name="bookingId">
            <Input type="text" placeholder="Buscar por booking Id" allowClear />
          </Form.Item>

          <Form.Item name="searchText">
            <Input type="text" placeholder="Buscar por nombre" allowClear />
          </Form.Item>
        </Form>
      </Col>

      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          loading={loading}
          rowKey="id"
          locale={{ emptyText: "No hay datos" }}
          dataSource={userRequestByDateValue}
          pagination={{
            total: userRequestByDateTotal,
            hideOnSinglePage: true,
            showSizeChanger: false,
            onChange: onPageChange,
            showTotal: (total) => `Total ${total} viajes`,
            size: "default",
          }}
          scroll={{ x: 600, y: "calc(-250px + 96vh)" }}
          onChange={onFilter}
        >
          <Column
            title="Booking Id"
            dataIndex="bookingId"
            width={100}
            fixed="left"
          />
          <Column title="Inicio" dataIndex="startedAt" width={100} />

          <Column
            title="Fin"
            dataIndex="finishedAt"
            width={100}
            render={(item) => {
              return item ?? NoFinishDate;
            }}
          />
          <Column title="Conductor" width={150} dataIndex="providerName" />
          <Column title="Descripción" width={200} dataIndex="description" />
          <Column title="Empresa" width={150} dataIndex="company" />
          <Column title="Departmento" width={150} dataIndex="department" />
          <Column title="Cliente" width={150} dataIndex="customerName" />
          <Column title="Pasajero" width={150} dataIndex="passengerName" />
          <Column title="Origen" width={250} dataIndex="originAddress" />
          <Column title="Destino" width={250} dataIndex="destinationAddress" />
          <Column
            title="Estado"
            width={100}
            dataIndex="status"
            render={(status) => (
              <Tag color={StatusColors[status]}>
                {IndexStatusTranslations[status]?.translation ?? status}
              </Tag>
            )}
            filters={[
              {
                value: "",
                text: "Ninguno",
              },
              {
                value: "ACCEPTED",
                text: "Aceptado",
              },
              {
                value: "SCHEDULED",
                text: "Agendado",
              },
              {
                value: "SEARCHING",
                text: "Buscando",
              },
              {
                value: "CANCELLED",
                text: "Cancelado",
              },
              {
                value: "STARTED",
                text: "Iniciado",
              },
              {
                value: "COMPLETED",
                text: "Completado",
              },
              {
                value: "ARRIVED",
                text: "Conductor llego",
              },
              {
                value: "CREATING",
                text: "Creando",
              },
              {
                value: "PICKEDUP",
                text: "Abordado",
              },
              {
                value: "DROPPED",
                text: "En destino",
              },
              {
                value: "FINISHED",
                text: "Finalizado",
              },
              {
                value: "TRAVELING",
                text: "En viaje",
              },
            ]}
            filterMultiple={false}
            defaultFilteredValue={[""]}
            filterResetToDefaultFilteredValue={true}
            filteredValue={[currentStatus]}
          />
          <Column
            title="Pagado"
            dataIndex="paid"
            width={150}
            render={(paid) =>
              paid === 1 ? PaidDescription : NotPaidDescription
            }
          />
          <Column
            title={`${filters?.shouldSendCompany ? "Total" : "Comisión"}`}
            width={150}
            dataIndex={`${filters?.shouldSendCompany ? "total" : "commision"}`}
          />
          <Column title="Minutos" dataIndex="minutes" width={150} />
          <Column
            title="Distancia"
            dataIndex="distance"
            width={150}
            render={(item, { estimatedDistance }: UserRequestByDateDTO) => {
              return item !== "0 km" ? item : estimatedDistance;
            }}
          />
          {(selectCurrentAdminType() === SuperAdminTypeId ||
            selectCurrentAdminType() === OperatorTypeId) && (
            <>
              <Column
                fixed="right"
                width={105}
                render={(record) => (
                  <Dropdown
                    menu={{
                      items: items(
                        record,
                        selectCurrentAdminType() === OperatorTypeId,
                      ),
                    }}
                    placement="bottom"
                    arrow
                  >
                    <Button>Acciones</Button>
                  </Dropdown>
                )}
              />
            </>
          )}
          {selectCurrentAdminType() === SuperAdminTypeId && (
            <Column
              fixed="right"
              width={200}
              render={(record) => {
                return (
                  StatusTranslations.find(
                    (x) => x.translation === record.status,
                  )?.name === TRIP_STATUS.Completed && (
                    <Button
                      onClick={() => {
                        navigate(
                          `${WebRoutes.AdminPanelTripDetails}/${record.id}`,
                          {
                            state: {
                              ...record,
                              navigationType: "SuperAdmin",
                            },
                            replace: true,
                          },
                        );
                      }}
                    >
                      Ver detalles del viaje
                    </Button>
                  )
                );
              }}
            />
          )}
          {selectCurrentAdminType() === AdminTypeId && (
            <>
              <Column title="Total Local" dataIndex="totalLocal" width={150} />
              <Column title="Moneda" dataIndex="currency" width={150} />
            </>
          )}
        </Table>
        <Modal
          title="Cierre de viaje"
          centered
          open={openModal}
          onOk={form.submit}
          onCancel={handleCloseModal}
        >
          <Form
            layout="vertical"
            name="trip"
            scrollToFirstError
            onFinish={calculateRequest}
            form={form}
            initialValues={{
              total: 0,
              currencyId: null,
            }}
          >
            <InlineForm>
              <Form.Item
                name="total"
                label="Total del viaje"
                tooltip="Total del viaje"
                rules={[{ required: true, message: "Ingrese una cantidad" }]}
              >
                <Input type="number" />
              </Form.Item>
              {currencies && (
                <Form.Item
                  name="currencyId"
                  label="Denominación"
                  tooltip="Enviar cierre denominacion seleccionada"
                  rules={[
                    {
                      required: true,
                      message: "Por favor seleccione un servicio!",
                    },
                  ]}
                >
                  <Select placeholder="Selecione un tipo">
                    {currencies.map((currency: CurrencyDTO) => (
                      <Select.Option key={currency?.id} value={currency?.id}>
                        {currency?.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </InlineForm>
          </Form>
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default UserRequestTable;
